import { Router } from '@angular/router';
import { Recording, RecordingsCommunity } from '@models/index';
import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { findAndRemove } from '../../../helpers';
import { RecordingsService } from '../recordings.service';
import { findAndReplaceByIdWithSort, RecordingsStore } from '../recordings.store';

export const getRecordingCommunityHandlingMethods = (injects: {
  store: RecordingsStore;
  recordingsService: RecordingsService;
  router: Router;
}) => {
  const { store, recordingsService, router } = injects;

  const selectRecordingCommunity = (communityId: string) => {
    patchState(store, { selectedRecordingCommunityId: communityId });
  };

  const addRecordingCommunities = rxMethod<{ recordingId: string; communities: string[] }>(
    pipe(
      tap(() =>
        patchState(store, {
          isSelectedRecordingLoading: true,
          isLoading: true,
        }),
      ),
      switchMap(({ recordingId, communities }) =>
        recordingsService.addCommunities(recordingId, communities).pipe(
          tap(recording => {
            const currentRecordings = store.recordings();
            const updatedRecordings = currentRecordings.map((r: Recording) => (r.id === recording.id ? recording : r));
            patchState(store, {
              selectedRecording: recording,
              recordings: updatedRecordings,
              isSelectedRecordingLoading: false,
              isLoading: false,
              selectedRecordingCommunityId: null,
            });
          }),
          catchError(() => {
            patchState(store, {
              isSelectedRecordingLoading: false,
              isLoading: false,
            });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const updateRecordingCommunity = rxMethod<{ recordingId: string; communityId: string; community: any }>(
    pipe(
      tap(() => patchState(store, { isSelectedRecordingLoading: true, isLoading: true })),
      switchMap(({ recordingId, communityId, community }) =>
        recordingsService.updateCommunity(recordingId, communityId, community).pipe(
          tap(recording => {
            const currentRecordings = store.recordings();
            const updatedRecordings = currentRecordings.map((r: Recording) => (r.id === recording.id ? recording : r));
            patchState(store, {
              selectedRecording: recording,
              recordings: updatedRecordings,
              isSelectedRecordingLoading: false,
              isLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedRecordingLoading: false, isLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const deleteRecordingCommunity = rxMethod<{ recordingId: string; communityId: string; isCommunityView?: boolean }>(
    pipe(
      tap(() =>
        patchState(store, {
          isSelectedRecordingLoading: true,
          isLoading: true,
        }),
      ),
      switchMap(({ recordingId, communityId, isCommunityView }) =>
        recordingsService.deleteCommunity(recordingId, communityId).pipe(
          tap(() => {
            const selectedRecording = store.selectedRecording()!;
            const updatedCommunities = findAndRemove<RecordingsCommunity>(
              selectedRecording.communities || [],
              item => item.id === communityId,
            );
            const updatedRecording = { ...selectedRecording, communities: updatedCommunities };
            patchState(store, {
              selectedRecording: isCommunityView ? null : updatedRecording,
              recordings: findAndReplaceByIdWithSort(store.recordings(), updatedRecording),
              recordingsByCommunity: findAndRemove<Recording>(store.recordingsByCommunity(), (r: Recording) => r.id === recordingId),
              isSelectedRecordingLoading: false,
              isLoading: false,
              isRecordingsByCommunityLoading: false,
            });
            if (isCommunityView) {
              router.navigate(['marketing', 'communities', communityId, 'recordings']);
            } else {
              router.navigate(['marketing', 'recordings', recordingId, 'communities']);
            }
          }),
          catchError(() => {
            patchState(store, {
              isSelectedRecordingLoading: false,
              isLoading: false,
            });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const approveRecordingCommunity = rxMethod<{ recordingId: string; communityId: string }>(
    pipe(
      tap(() =>
        patchState(store, {
          isLoading: true,
          isRecordingsByCommunityLoading: true,
          isSelectedRecordingLoading: true,
        }),
      ),
      switchMap(({ recordingId, communityId }) =>
        recordingsService.approveCommunity(recordingId, communityId).pipe(
          tap(recording => {
            patchState(store, {
              selectedRecording: recording,
              recordings: findAndReplaceByIdWithSort(store.recordings(), recording),
              recordingsByCommunity: findAndReplaceByIdWithSort(store.recordingsByCommunity(), recording),
              isSelectedRecordingLoading: false,
              isLoading: false,
              isRecordingsByCommunityLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, {
              isSelectedRecordingLoading: false,
              isLoading: false,
              isRecordingsByCommunityLoading: false,
            });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  return {
    selectRecordingCommunity,
    addRecordingCommunities,
    updateRecordingCommunity,
    deleteRecordingCommunity,
    approveRecordingCommunity,
  };
};

export interface RecordingCommunityHandlingStoreType {
  readonly selectRecordingCommunity: (communityId: string) => void;
  readonly addRecordingCommunities: (input: { recordingId: string; communities: string[] }) => void;
  readonly updateRecordingCommunity: (input: { recordingId: string; communityId: string; community: any }) => void;
  readonly deleteRecordingCommunity: (input: { recordingId: string; communityId: string; isCommunityView?: boolean }) => void;
  readonly approveRecordingCommunity: (input: { recordingId: string; communityId: string }) => void;
}
